import { useContext, useEffect, useMemo } from 'react';
import { ButtonBase, Grid, Typography } from '@mui/material';
import { GridOn, PictureAsPdf } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { dropFretePorConta } from 'utils/drops';
import { DropsContext } from 'contexts/DropsContext';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Input from 'components/Input';

const CotacaoDetalheModal = ({ requisicao_id, documento_id, item_id }) => {
  const { drops } = useContext(DropsContext);
  const { getURLRelatorio } = useContext(RelatoriosContext);
  const { documento, getLoading, getDocumento, postLoading, postDocumento } =
    useContext(DocumentosContext);
  const defaultValues = {
    cadastro_id: null,
    prazo: '',
    pessoa_contato: '',
    contato: '',
    validade: '',
    przent: null,
    fretepc: null,
    preco: '',
    desconto1_pc: '',
    icms: '',
    subtrib: '',
    ipi: '',
    frete: '',
  };
  const { control, setValue, handleSubmit } = useForm({ defaultValues });
  const item = useMemo(() => {
    return (
      documento?.DocumentoItems?.find((i) =>
        i?.DocumentoItemOrigems?.some(
          (s) => s?.documento_item_org_id == item_id
        )
      ) || null
    );
  }, [documento, item_id]);
  const pc_frete = useMemo(() => {
    if (!item) return '';
    return (
      ((item.frete || 0) / ((item.preco || 0) * (item.quantidade || 0))) * 100
    );
  }, [item]);
  const icms = useMemo(
    () => item?.DocumentoItemImpostos?.find((f) => f?.tributo_id === 1),
    [item]
  );
  const subtrib = useMemo(
    () => item?.DocumentoItemImpostos?.find((f) => f?.tributo_id === 2),
    [item]
  );
  const ipi = useMemo(
    () => item?.DocumentoItemImpostos?.find((f) => f?.tributo_id === 3),
    [item]
  );

  const loadData = () => getDocumento(documento_id);

  const onPrintPDF = () => {
    getURLRelatorio({
      data: { codigo: 'DOC0000002', documento: documento_id, file: 'PDF' },
    });
  };

  const onPrintXLS = () => {
    getURLRelatorio({
      data: { codigo: 'DOC0000002', documento: documento_id, file: 'XLSX' },
    });
  };

  const onPrintMapa = () => {
    getURLRelatorio({ data: { id: 149, documento_id: requisicao_id } });
  };

  const onSubmit = (values) => {
    const frete =
      ((item?.preco || 0) * (item?.quantidade || 0) * (values?.frete || 0)) /
      100;
    const data = {
      Documento: {
        id: documento_id,
      },
      CotacaoCompra: {
        documento_id,
        prazo: values?.prazo,
        pessoa_contato: values?.pessoa_contato,
        contato: values?.contato,
        validade: values?.validade,
      },
      Itens: [
        {
          id: item?.id,
          documento_id,
          preco: values?.preco,
          desconto1_pc: values?.desconto1_pc,
          przent: values?.przent,
          frete,
          Impostos: [
            ...(values?.icms
              ? [
                  {
                    ...icms,
                    tributo_id: 1,
                    percentual: values.icms,
                    base_calculo: (values.preco || 0) * (item?.quantidade || 0),
                    valor:
                      ((values.preco || 0) *
                        (item?.quantidade || 0) *
                        values.icms) /
                      100,
                  },
                ]
              : []),

            ...(values?.subtrib
              ? [
                  {
                    ...subtrib,
                    tributo_id: 2,
                    percentual: values.subtrib,
                    base_calculo: (values.preco || 0) * (item?.quantidade || 0),
                    valor:
                      ((values.preco || 0) *
                        (item?.quantidade || 0) *
                        values.subtrib) /
                      100,
                  },
                ]
              : []),

            ...(values?.ipi
              ? [
                  {
                    ...ipi,
                    tributo_id: 3,
                    percentual: values.ipi,
                    base_calculo: (values.preco || 0) * (item?.quantidade || 0),
                    valor:
                      ((values.preco || 0) *
                        (item?.quantidade || 0) *
                        values.ipi) /
                      100,
                  },
                ]
              : []),
          ],
        },
      ],
      ...(Boolean(values?.fretepc) && {
        Transportes: [
          {
            id: documento?.DocumentoTransportes?.[0]?.id || null,
            documento_id,
            tipotransp: 'DESPACHO',
            fretepc: values?.fretepc,
          },
        ],
      }),
    };

    postDocumento({ data, cb: loadData });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const item =
      documento?.DocumentoItems?.find((i) =>
        i?.DocumentoItemOrigems?.some(
          (s) => s?.documento_item_org_id == item_id
        )
      ) || null;
    if (!documento) return;

    setValue('cadastro_id', documento.cadastro_id || null);
    setValue('prazo', documento?.Cocotacao?.prazo || '');
    setValue('pessoa_contato', documento?.Cocotacao?.pessoa_contato || '');
    setValue('contato', documento?.Cocotacao?.contato || '');
    setValue('validade', documento?.Cocotacao?.validade || '');

    setValue('przent', item?.przent || null);
    setValue('fretepc', documento?.DocumentoTransportes?.[0]?.fretepc || null);

    setValue('preco', item?.preco || '');
    setValue('desconto1_pc', item?.desconto1_pc || '');

    setValue('icms', icms?.percentual || '');
    setValue('subtrib', subtrib?.percentual || '');
    setValue('ipi', ipi?.percentual || '');

    setValue('frete', pc_frete || '');
  }, [documento, item, icms, subtrib, ipi, pc_frete, setValue]);

  if (getLoading) return <Loader />;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" component="div" align="center">
          Informações da Cotação
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Dropdown
          disabled
          name="cadastro_id"
          control={control}
          label="Fornecedor"
          options={drops.Entidade}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Input name="prazo" label="Prazo" control={control} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Input
          control={control}
          name="pessoa_contato"
          label="Pessoa de Contato"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask
          name="contato"
          control={control}
          label="Contato"
          mask="(99) 99999-9999"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask name="validade" control={control} label="Validade (Dias)" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask
          type="date"
          name="przent"
          control={control}
          label="Prazo de Entrega"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Dropdown
          name="fretepc"
          control={control}
          label="Frete por Conta"
          options={dropFretePorConta}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" component="div" align="center">
          {item?.descricao}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputMask name="preco" control={control} label="Preço" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputMask name="desconto1_pc" control={control} label="% Desconto" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="icms" control={control} label="% ICMS" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask
          name="subtrib"
          control={control}
          label="% Substituição Tributária"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="ipi" control={control} label="% IPI" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask name="frete" control={control} label="% Frete" />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          color="primary"
          variant="contained"
          loading={postLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Salvar
        </Button>
      </Grid>
      <Grid item xs={12} mt={5}>
        <Typography variant="h6" component="div">
          Impressão da Cotação
        </Typography>
      </Grid>
      <Grid item xs={12} gap={5} display="flex">
        <ButtonBase
          onClick={onPrintPDF}
          sx={{
            flexDirection: 'column',
            '& svg': { fontSize: 80, color: 'error.main' },
          }}
        >
          <PictureAsPdf />
          <Typography variant="subtitle2">PDF</Typography>
        </ButtonBase>
        <ButtonBase
          onClick={onPrintXLS}
          sx={{
            flexDirection: 'column',
            '& svg': { fontSize: 80, color: 'success.main' },
          }}
        >
          <GridOn />
          <Typography variant="subtitle2">Excel</Typography>
        </ButtonBase>
      </Grid>
      <Grid item xs={12} mt={5}>
        <Typography variant="h6" component="div">
          Mapa da Cotação
        </Typography>
      </Grid>
      <Grid item xs={12} gap={5} display="flex">
        <ButtonBase
          onClick={onPrintMapa}
          sx={{
            flexDirection: 'column',
            '& svg': { fontSize: 80, color: 'error.main' },
          }}
        >
          <PictureAsPdf />
          <Typography variant="subtitle2">PDF</Typography>
        </ButtonBase>
      </Grid>
    </Grid>
  );
};

export default CotacaoDetalheModal;
